import type { SystemSurveyComment } from '@engagement-web-app/common';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { Download } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Button } from '@/components/Button';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Pagination } from '@/components/Pagination';
import { EditCommentDialog } from '@/components/surveys/comments/EditCommentDialog';
import { SurveyCommentsTable } from '@/components/surveys/comments/SurveyCommentsTable';
import { CreatePdfButton } from '@/components/surveys/common/CreatePdfButton';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { useTopRef } from '@/hooks';
import { buildSurveyCommentsExportUrl, fetchSurveyComments, updateSurveyComment } from '@/queries/surveys/system';
import { hiddenInPrint } from '@/styles/print.css';
import { combineQueryErrors, debounce } from '@/utils';
import { useAllCommentTags, useCommentTags, useSurveyCommentQuestions } from '../-hooks';
import { DefaultRouteQueryParams, validateSearch } from '../-search';
import * as styles from './index.css';

const COMMENTS_PAGE_SIZE = 50;

// Unlike the other tabs this one works off the selected survey alone, so there's
// no loader — the survey list isn't needed here.
export const Route = createFileRoute('/system/_auth/surveys/comments/')({
    validateSearch,
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    component: SurveyCommentsRoute,
});

export function SurveyCommentsRoute() {
    const { organizationId, surveyId: selectedSurveyId } = Route.useSearch();

    // Re-mount Content when the survey changes to reset all filter state.
    return <Content key={selectedSurveyId} organizationId={organizationId} selectedSurveyId={selectedSurveyId} />;
}

type ContentProps = {
    organizationId: string;
    selectedSurveyId: string;
};

function Content({ organizationId, selectedSurveyId }: ContentProps) {
    const [page, setPage] = useState(1);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string>('');
    const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
    // Two search states: `searchText` mirrors the input value for a responsive
    // UI, `debouncedSearchText` is the value that drives the actual query so we
    // don't re-fetch on every keystroke.
    const [searchText, setSearchText] = useState<string>('');
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

    const { topRef, TopRef } = useTopRef();

    const queryClient = useQueryClient();
    const [editTarget, setEditTarget] = useState<SystemSurveyComment | null>(null);

    const editMutation = useMutation({
        mutationFn: (values: { answer: string; sentiment: SystemSurveyComment['sentiment']; tagIds: string[] }) =>
            updateSurveyComment(organizationId, selectedSurveyId, editTarget!.answerId, editTarget!.questionId, values),
        onSuccess: () => {
            setEditTarget(null);
            queryClient.invalidateQueries({ queryKey: ['surveyComments'] });
            showSuccessToast({ title: 'コメント編集', description: 'コメントを更新しました。' });
        },
        onError: () => {
            showErrorToast({ title: 'コメント編集', description: 'コメントの更新に失敗しました。' });
        },
    });

    const questionsQuery = useSurveyCommentQuestions(organizationId, selectedSurveyId);
    const tagCatalogueQuery = useCommentTags(organizationId, selectedSurveyId);
    // The filter lists the survey's tags; the edit dialog offers every tag.
    const allTagsQuery = useAllCommentTags();

    const debouncedTextSearch = useCallback(
        debounce((text: string) => {
            setDebouncedSearchText(text);
            setPage(1);
        }, 300),
        []
    );

    const commentsQuery = useQuery({
        queryKey: [
            'surveyComments',
            organizationId,
            selectedSurveyId,
            selectedQuestionId,
            selectedTagIds,
            debouncedSearchText,
            page,
        ],
        queryFn: () =>
            fetchSurveyComments(organizationId, selectedSurveyId, {
                questionIds: selectedQuestionId ? [selectedQuestionId] : undefined,
                tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
                search: debouncedSearchText || undefined,
                page,
                pageSize: COMMENTS_PAGE_SIZE,
            }),
        placeholderData: keepPreviousData,
    });

    const { isError, retry } = combineQueryErrors(questionsQuery, tagCatalogueQuery, commentsQuery);

    const comments = commentsQuery.data?.comments ?? null;
    const lastPage = Math.ceil((commentsQuery.data?.total ?? 0) / COMMENTS_PAGE_SIZE);

    const handlePageChange = (nextPage: number) => {
        setPage(nextPage);
        topRef.current?.scrollIntoView({ block: 'start' });
    };

    return (
        <div>
            <Flex justify="end" gap="4" className={classNames(styles.actions, hiddenInPrint)}>
                <Button
                    variant="secondary"
                    onClick={() => {
                        // Navigating to the endpoint lets the browser save the
                        // file; the response is served as an attachment.
                        window.location.href = buildSurveyCommentsExportUrl(organizationId, selectedSurveyId, {
                            questionIds: selectedQuestionId ? [selectedQuestionId] : undefined,
                            tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
                            search: debouncedSearchText || undefined,
                        });
                    }}
                >
                    <Icon icon={Download} size="sm" />
                    CSVをダウンロードする
                </Button>
            </Flex>
            <Flex justify="end" className={classNames(styles.actions, hiddenInPrint)}>
                <CreatePdfButton />
            </Flex>

            <TopRef />

            <SurveyCommentsTable
                comments={comments}
                questions={questionsQuery.data ?? null}
                selectedQuestionId={selectedQuestionId}
                onSelectedQuestionIdChange={(id) => {
                    setSelectedQuestionId(id);
                    setPage(1);
                }}
                tagCatalogue={tagCatalogueQuery.data ?? null}
                selectedTagIds={selectedTagIds}
                onSelectedTagIdsChange={(ids) => {
                    setSelectedTagIds(ids);
                    setPage(1);
                }}
                searchText={searchText}
                onSearchTextChange={(text) => {
                    setSearchText(text);
                    debouncedTextSearch(text);
                }}
                isFetching={commentsQuery.isFetching}
                isError={isError}
                onRetry={retry}
                onEditComment={setEditTarget}
            />

            <EditCommentDialog
                comment={editTarget}
                catalogue={allTagsQuery.data ?? null}
                isSubmitting={editMutation.isPending}
                onOpenChange={(open) => !open && setEditTarget(null)}
                onSubmit={async (values) => {
                    await editMutation.mutateAsync(values);
                }}
            />

            {!isError && (
                <Flex justify="center">
                    <Pagination currentPage={page} lastPage={lastPage} onPageChange={handlePageChange} />
                </Flex>
            )}
        </div>
    );
}
